import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { colors } from '../../../../styles/globals/_variables'
import { useReports } from '../../Context/ReportsContext'
import {
    fetchRequestsReportData,
    fetchRequestsReportCsv,
    fetchNetworkRequestsReportData,
    fetchNetworkRequestsReportCSV,
} from '../apis/api-reports'
import { useCohorts } from '../../Hooks/useCohorts'
import DayJS from '../../../../helpers/DayJS'
import { useReportCSVDownload } from './useReportCSVDownload'
import { ReportProps } from '../Components/Report'
import { defaultStackedColumnChart } from '../Components/config/default.column.chart'
import { useTranslateChartHeaders } from './useTranslatedChartData'

export const useHelpRequestsReport = () => {
    const { t } = useTranslation('reports-ai')
    const { reportQuery, enabled, staleTime, buildReportsQueryKey, calculateChartYAxisMax, isNetworkContext, networkId } =
        useReports()
    const { cohorts, from, to } = reportQuery
    const { networkCohorts } = useCohorts()
    const { eggColor, active } = colors

    const section = 'AI'
    const report = 'Help Requests'
    const title = t(report)

    // Get courseIds from network cohorts if in network context
    const selectedCohortIds = isNetworkContext
        ? networkCohorts
              ?.filter((nc) => cohorts?.length === 0 || cohorts?.includes(nc.startDate.substring(0, 10)))
              .map((c) => c.courseIdMongo) || []
        : []

    // Choose API based on context
    const fetchFunction = isNetworkContext
        ? () => fetchNetworkRequestsReportData({ networkId: networkId as string, cohorts: selectedCohortIds, from, to })
        : () => fetchRequestsReportData(reportQuery)

    const queryKey = isNetworkContext
        ? [networkId, 'Network Reports', section, report, `Cohorts: ${selectedCohortIds.join()}`, from, to]
        : buildReportsQueryKey({ section, report })

    const { data, ...rest } = useQuery(queryKey, fetchFunction, {
        enabled: isNetworkContext ? !!networkId && selectedCohortIds.length > 0 : enabled,
        staleTime: isNetworkContext ? 0 : staleTime,
    })

    const vAxisTitle = `${t('total help requests')}: ${data?.totalHelpRequests ?? 0}`
    const viewWindow = useMemo(() => calculateChartYAxisMax({ rows: data?.barChart }), [data, calculateChartYAxisMax])
    
    const chart: ReportProps['chart'] = {
        ...defaultStackedColumnChart,
        data: useTranslateChartHeaders({ chartData: data?.barChart, i18nNamespace: 'reports-ai' }),
        options: {
            ...defaultStackedColumnChart.options,
            colors: [eggColor, active],
            vAxis: {
                title: vAxisTitle,
                viewWindow,
            },
        },
    }

    // CSV download
    const fileName = `help-requests_${DayJS().format('YYYY-MM-DD_HH-mm')}.csv`
    const { onExportCSV, exporting } = useReportCSVDownload({
        downloadFn: isNetworkContext
            ? () => fetchNetworkRequestsReportCSV({ networkId: networkId as string, cohorts: selectedCohortIds, from, to })
            : () => fetchRequestsReportCsv(reportQuery),
        fileName,
    })

    const chartProps: ReportProps = {
        title,
        chart,
        onExportCSV,
        exportDisabled: exporting || rest.isLoading,
        loading: rest.isLoading,
    }

    return { chartProps, data, ...rest }
}
